import { Button, FileTrigger, FileTriggerProps } from 'react-aria-components';
import { Icon } from '@designsystem/stories/02-atoms/Icon';
import { Formfield } from '@designsystem/stories/03-molecules/Formfield';

export type FileSelectorProps = {
  /**
   * Label for the button that triggers the file selector.
   */
  labelButton?: string;
  /**
   * Label for the selected file and also if not file is selected.
   */
  labelFile?: string;
  /**
   * Optional flag to determine whether a close button should be shown on the file selector.
   * If true, a close button will be displayed; if false or undefined, it will not.
   */
  showCloseButton?: boolean;
  /**
   * Flag to indicate if there is an error in the file selection process.
   * If true, an error state will be displayed; if false or undefined, it will not.
   */
  hasError?: boolean;
  /**
   * Aria label for the delete button.
   */
  ariaLabelDeleteButton?: string;
  /**
   * Callback function to be called when the delete button is clicked.
   */
  /**
   * A label for the input field.
   */
  label?: string;
  /**
   * A label, indicating that the field is required.
   */
  labelRequired?: string;
  /**
   * Description or additional information about the input field.
   */
  description?: string;
  /**
   * An error message to display when validation fails.
   * It can be either a string or a function that takes validation result as argument and returns a string.
   */
  errorMessage?: string;
  /**
   * Indicates whether the input field is required or not.
   */
  isRequired?: boolean;
  /**
   * Which values to accept.
   */
  accept?: string;
  deleteFilesCallback?: () => void;
} & FileTriggerProps;

const FileSelector = ({
  labelButton = 'Choose File',
  labelFile = 'No file chosen',
  showCloseButton = true,
  hasError = false,
  deleteFilesCallback = () => {},
  ariaLabelDeleteButton = 'Remove selected files',
  label,
  labelRequired,
  description,
  errorMessage,
  isRequired = false,
  accept = undefined,
  ...props
}: FileSelectorProps) => {
  return (
    <Formfield
      label={label}
      description={description}
      isRequired={isRequired}
      labelRequired={labelRequired}
      errorMessage={errorMessage}
      className="file-selector"
    >
      <FileTrigger
        {...props}
        {...(accept ? { acceptedFileTypes: [accept] } : {})}
      >
        <Button className="file-selector__choose btn btn--secondary btn--small">
          {labelButton}
        </Button>
      </FileTrigger>
      <div className="file-selector__filename" aria-live="polite">
        {labelFile}
      </div>
      {showCloseButton && labelFile && (
        <button
          type="button"
          title={ariaLabelDeleteButton}
          onClick={deleteFilesCallback}
          className="file-selector__remove"
        >
          <Icon icon="cancel" />
        </button>
      )}
    </Formfield>
  );
};

export default FileSelector;
